import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, 
  LogOut, 
  Loader2, 
  Headphones, 
  Settings, 
  FileText, 
  Shield, 
  Database,
  Zap,
  BarChart3,
  Clock,
  Brain,
  Package,
  Users,
  CreditCard,
  RefreshCw,
  CheckCircle2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TurnstileWidget } from '@/components/Onboarding/TurnstileWidget';
import { useLicenseStatus } from '@/hooks/useLicenseStatus';

// Mapeamento de ícones por código do módulo
const MODULE_ICONS: Record<string, React.ReactNode> = {
  incidents: <AlertTriangle className="h-5 w-5" />,
  requests: <Headphones className="h-5 w-5" />,
  problems: <Shield className="h-5 w-5" />,
  changes: <Settings className="h-5 w-5" />,
  asset_management: <Database className="h-5 w-5" />,
  software_management: <Package className="h-5 w-5" />,
  knowledge_base: <FileText className="h-5 w-5" />,
  reports: <BarChart3 className="h-5 w-5" />,
  forms: <FileText className="h-5 w-5" />,
  sla_management: <Clock className="h-5 w-5" />,
  ai_integrated: <Brain className="h-5 w-5" />,
};

interface ModulePrice {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  is_active: boolean;
  icon: string | null;
  display_order: number;
}

interface LicenseTier {
  id: string;
  name: string;
  min_licenses: number;
  max_licenses: number;
  is_active: boolean;
  display_order: number;
}

const LicenseExpired = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [isAnnual, setIsAnnual] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [licenseQuantity, setLicenseQuantity] = useState<number>(1);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string>('');
  const [showReactivated, setShowReactivated] = useState(false);

  // State para forçar refresh do hook
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Hook para verificar status da licença
  const licenseStatus = useLicenseStatus(clientId, refreshKey);

  // Redirecionar automaticamente se a licença estiver válida
  useEffect(() => {
    if (!licenseStatus.isLoading && licenseStatus.isValid && !licenseStatus.isExpired) {
      setShowReactivated(true);
      const timer = setTimeout(() => {
        navigate('/portal');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    clientId,
    licenseStatus.isLoading,
    licenseStatus.isValid,
    licenseStatus.isExpired,
    licenseStatus.status,
    licenseStatus.expiresAt,
    licenseStatus.daysUntilExpiry,
    navigate,
  ]);

  // Função para verificar novamente (sincronizar com Stripe)
  const handleVerifyAgain = useCallback(async () => {
    if (!clientId) return;
    
    setIsVerifying(true);
    try {
      // Chamar check-subscription para sincronizar com o Stripe
      const { error } = await supabase.functions.invoke('check-subscription', {});
      
      if (error) {
        console.warn('[LicenseExpired] Erro ao sincronizar com Stripe:', error);
      }

      // Invalidar cache de módulos
      await queryClient.invalidateQueries({ queryKey: ['client-modules-expired', clientId] });
      
      // Forçar refetch do hook useLicenseStatus incrementando a key
      setRefreshKey(prev => prev + 1);
      
      // Aguardar um momento para o hook processar
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: 'Verificação concluída',
        description: 'Status atualizado. Aguarde...',
      });
    } catch (error) {
      console.error('[LicenseExpired] Erro verificação:', error);
      toast({
        title: 'Erro na verificação',
        description: 'Não foi possível verificar o status. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  }, [clientId, queryClient, toast]);

  // Buscar dados do usuário e cliente
  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth');
        return;
      }

      setUserEmail(session.user.email || null);

      // Buscar profile com client_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('id', session.user.id)
        .single();

      if (profile?.client_id) {
        setClientId(profile.client_id);

        // Buscar nome do cliente
        const { data: client } = await supabase
          .from('clients')
          .select('name, razao_social')
          .eq('id', profile.client_id)
          .single();

        if (client) {
          setClientName(client.razao_social || client.name || 'Cliente');
        }
      }
    };

    fetchUserData();
  }, [navigate]);

  // Buscar módulos já ativos do cliente
  const { data: clientModules = [] } = useQuery({
    queryKey: ['client-modules-expired', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('client_modules')
        .select('module_code')
        .eq('client_id', clientId)
        .eq('is_active', true);

      if (error) throw error;
      return data.map(m => m.module_code);
    },
    enabled: !!clientId,
  });

  // Pré-selecionar módulos que o cliente já tinha
  useEffect(() => {
    if (clientModules.length > 0 && selectedModules.length === 0) {
      setSelectedModules(clientModules);
    }
  }, [clientModules]);

  // Buscar todos os módulos disponíveis
  const { data: modules = [], isLoading: isLoadingModules } = useQuery({
    queryKey: ['module_prices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_prices')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ModulePrice[];
    },
  });

  // Buscar tiers de licença
  const { data: licenseTiers = [], isLoading: isLoadingTiers } = useQuery({
    queryKey: ['license_tiers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('license_tiers')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as LicenseTier[];
    },
  });

  // Definir tier padrão quando carregar
  useEffect(() => {
    if (licenseTiers.length > 0 && !selectedTierId) {
      const defaultTier = licenseTiers[0];
      setSelectedTierId(defaultTier.id);
      setLicenseQuantity(defaultTier.max_licenses);
    }
  }, [licenseTiers, selectedTierId]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const handleModuleToggle = (code: string) => {
    setSelectedModules((prev) =>
      prev.includes(code)
        ? prev.filter((m) => m !== code)
        : [...prev, code]
    );
  };

  const handleTierChange = (tierId: string) => {
    setSelectedTierId(tierId);
    const tier = licenseTiers.find(t => t.id === tierId);
    if (tier) {
      setLicenseQuantity(tier.max_licenses);
    }
  };

  // Calcular soma dos módulos selecionados
  const calculateModulesSum = () => {
    return selectedModules.reduce((sum, code) => {
      const module = modules.find((m) => m.code === code);
      return sum + (module?.price || 0);
    }, 0);
  };

  // Calcular total: (Soma Módulos) × (Quantidade Licenças)
  const calculateTotal = () => {
    const moduleSum = calculateModulesSum();
    const total = moduleSum * licenseQuantity;
    
    // Se anual, aplica desconto (10 meses em vez de 12)
    return isAnnual ? total * 10 : total;
  };

  const handleAcquireLicense = async () => {
    if (selectedModules.length === 0) {
      toast({
        title: 'Selecione módulos',
        description: 'Escolha pelo menos um módulo para continuar.',
        variant: 'destructive',
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: 'Verificação necessária',
        description: 'Complete a verificação de segurança.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Chamar edge function para criar checkout
      const { data, error } = await supabase.functions.invoke('create-modular-checkout', {
        body: {
          selectedModules,
          licenseQuantity,
          billingCycle: isAnnual ? 'yearly' : 'monthly',
          clientId,
          email: userEmail,
          clientName,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de checkout não retornada');
      }

    } catch (error: any) {
      console.error('[LicenseExpired] Erro checkout:', error);
      toast({
        title: 'Erro ao iniciar pagamento',
        description: error.message || 'Tente novamente.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  // Loading inicial
  if (isLoadingModules || isLoadingTiers || licenseStatus.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Mostrar tela de "reativada" antes de redirecionar
  if (showReactivated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-950/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Licença Reativada!</h2>
            <p className="text-muted-foreground">
              Sua licença está ativa novamente. Redirecionando para o portal...
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl shadow-lg">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold">Licença Expirada</CardTitle>
          <CardDescription className="text-base">
            Seu período de avaliação ou assinatura expirou. Adquira uma licença para continuar 
            utilizando o sistema. Abaixo estão os módulos que você já utilizava.
          </CardDescription>

          {/* Botão verificar novamente */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleVerifyAgain}
            disabled={isVerifying}
            className="gap-2"
          >
            {isVerifying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isVerifying ? 'Verificando...' : 'Já paguei, verificar novamente'}
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Toggle Anual/Mensal */}
          <div className="flex items-center justify-center gap-4 p-4 bg-muted/50 rounded-lg">
            <span className={!isAnnual ? "font-medium" : "text-muted-foreground"}>
              Mensal
            </span>
            <Switch
              checked={isAnnual}
              onCheckedChange={setIsAnnual}
            />
            <span className={isAnnual ? "font-medium" : "text-muted-foreground"}>
              Anual
            </span>
            {isAnnual && (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                2 meses grátis
              </Badge>
            )}
          </div>

          {/* Lista de Módulos */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Zap className="h-4 w-4" />
              1. Confirme ou Altere os Módulos
            </Label>
            <p className="text-sm text-muted-foreground mb-3">
              Os módulos que você utilizava estão pré-selecionados. Você pode adicionar ou remover módulos.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {modules.map((module) => {
                const isSelected = selectedModules.includes(module.code);
                const wasActive = clientModules.includes(module.code);
                const icon = MODULE_ICONS[module.code] || <Settings className="h-4 w-4" />;
                const isFree = module.price === 0;
                
                return (
                  <div
                    key={module.code}
                    className={`cursor-pointer transition-all rounded-lg border p-3 ${
                      isSelected
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => handleModuleToggle(module.code)}
                  >
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={isSelected}
                        className="pointer-events-none mt-0.5 h-4 w-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`p-1 rounded ${
                            isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                          }`}>
                            {icon}
                          </span>
                          <h4 className="font-medium text-sm truncate">{module.name}</h4>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs">
                          {isFree ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs px-1.5 py-0">
                              Incluso
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">
                              R$ {module.price}/lic/mês
                            </span>
                          )}
                          {wasActive && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              Anterior
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Seletor de Quantidade de Licenças */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" />
              2. Quantidade de Licenças
            </Label>
            <p className="text-sm text-muted-foreground">
              Licenças são cobradas por usuário com função diferente de "Usuário Padrão"
            </p>
            
            <div className="space-y-2">
              <Label>Quantidade de Licenças</Label>
              <Select value={selectedTierId || ""} onValueChange={handleTierChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a quantidade" />
                </SelectTrigger>
                <SelectContent>
                  {licenseTiers.map((tier) => (
                    <SelectItem key={tier.id} value={tier.id}>
                      {tier.name} ({tier.min_licenses} - {tier.max_licenses} licenças)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Cobrança: <span className="font-medium text-foreground">{licenseQuantity} licenças</span>
              </p>
            </div>
          </div>

          {/* Resumo */}
          {selectedModules.length > 0 && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
              <h4 className="font-semibold">Resumo da Licença</h4>
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Módulos selecionados:</span>
                  <span>{selectedModules.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor por licença:</span>
                  <span>R$ {calculateModulesSum().toLocaleString("pt-BR")}/mês</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quantidade de licenças:</span>
                  <span>{licenseQuantity}</span>
                </div>
              </div>
              
              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      R$ {calculateModulesSum().toLocaleString("pt-BR")} × {licenseQuantity} licenças
                    </p>
                    <p className="text-2xl font-bold">
                      R$ {calculateTotal().toLocaleString("pt-BR")}
                      <span className="text-sm font-normal text-muted-foreground">
                        /{isAnnual ? "ano" : "mês"}
                      </span>
                    </p>
                  </div>
                  {isAnnual && (
                    <Badge className="bg-green-100 text-green-700">
                      Economia de R$ {(calculateModulesSum() * licenseQuantity * 2).toLocaleString("pt-BR")}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* CAPTCHA */}
          <div className="flex justify-center">
            <TurnstileWidget onVerify={setCaptchaToken} />
          </div>

          {/* Botões */}
          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleAcquireLicense}
              disabled={isLoading || selectedModules.length === 0 || !captchaToken}
              className="w-full gap-2"
              size="lg"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              Adquirir Licença
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair da Conta
            </Button>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            Precisa de ajuda?{' '}
            <a 
              href="mailto:suporte@aroan.com.br" 
              className="text-primary hover:underline"
            >
              Entre em contato
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LicenseExpired;
